/**
 * GramCore Public Website - Main Application Component
 * 
 * Simplified application focused exclusively on the public-facing website.
 * Customer Portal and Admin Dashboard have been moved to separate projects.
 * 
 * Features:
 * - Theme switching support
 * - Route-based code splitting for optimal performance
 * - Lazy loading for page components
 * - Error boundaries for graceful error handling
 * - Automatic scroll-to-top on navigation
 * 
 * @author GramCore Development Team
 * @version 2.0.0
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Context Providers
import { ThemeProvider } from './context/ThemeContext';

// Error Handling
import ErrorBoundary from './components/ErrorBoundary';

// Layout Components (eagerly loaded - needed immediately)
import ScrollToTop from './components/ScrollToTop';
import PublicLayout from './components/layouts/PublicLayout';
import PageLoadingOverlay from './components/PageLoadingOverlay';

// Homepage (eagerly loaded for fast initial render)
import PublicHome from './pages/public/Home';

// Lazy-loaded pages (loaded on demand)
// Main Public Pages
const QuoteRequest = lazy(() => import('./pages/public/QuoteRequest'));
const NotFound = lazy(() => import('./pages/public/NotFound'));

// Authentication
const AuthPage = lazy(() => import('./pages/public/auth/AuthPage'));

// Company Information
const OutletsPage = lazy(() => import('./pages/public/outlets/OutletsPage'));
const AboutPage = lazy(() => import('./pages/public/about/AboutPage'));
const HistoryPage = lazy(() => import('./pages/public/about/HistoryPage'));
const ContactPage = lazy(() => import('./pages/public/contact/ContactPage'));
const ArticlesPage = lazy(() => import('./pages/public/articles/ArticlesPage'));
const ArticleDetailPage = lazy(() => import('./pages/public/articles/ArticleDetailPage'));

// Product Specifications & Documentation
const ColoursPage = lazy(() => import('./pages/public/specification/ColoursPage'));
const TechnicalPage = lazy(() => import('./pages/public/specification/TechnicalPage'));
const BrochuresPage = lazy(() => import('./pages/public/specification/BrochuresPage'));
const InstallationPage = lazy(() => import('./pages/public/specification/InstallationPage'));
const WarrantyPage = lazy(() => import('./pages/public/specification/WarrantyPage'));
const VideosPage = lazy(() => import('./pages/public/specification/VideosPage'));

// Public Product Catalog
const SPFSteel = lazy(() => import('./pages/public/products/SPFSteel'));
const SteelPlinth = lazy(() => import('./pages/public/products/SteelPlinth'));
const GramLat3D = lazy(() => import('./pages/public/products/GramLat3D'));
const FloodwayLouvres = lazy(() => import('./pages/public/products/FloodwayLouvres'));
const GramlineInfillSheet = lazy(() => import('./pages/public/products/GramlineInfillSheet'));
const ColourLineInfillSheet = lazy(() => import('./pages/public/products/ColourLineInfillSheet'));
const SlimlineFraming = lazy(() => import('./pages/public/products/SlimlineFraming'));
const SmartPostFramingPost = lazy(() => import('./pages/public/products/SmartPostFramingPost'));
const SteelPlinthRetainingWalls = lazy(() => import('./pages/public/products/SteelPlinthRetainingWalls'));
const GatesByGramline = lazy(() => import('./pages/public/products/GatesByGramline'));
const PublicAccessories = lazy(() => import('./pages/public/products/Accessories'));
const SmartSlat = lazy(() => import('./pages/public/products/SmartSlat'));
const GramslatExtensionKit = lazy(() => import('./pages/public/products/GramslatExtensionKit'));
const SecurityFencing = lazy(() => import('./pages/public/products/SecurityFencing'));
const Hardware = lazy(() => import('./pages/public/products/Hardware'));
const ChildgardPoolFencing = lazy(() => import('./pages/public/products/ChildgardPoolFencing'));
const ChildgardGardenFencing = lazy(() => import('./pages/public/products/ChildgardGardenFencing'));
const GramLat3DPunchedLattice = lazy(() => import('./pages/public/products/GramLat3DPunchedLattice'));
const ChildgardPoolFencingStandardStyle = lazy(() => import('./pages/public/products/ChildgardPoolFencingStandardStyle'));
const ChildgardPoolFencingDecorativeStyle = lazy(() => import('./pages/public/products/ChildgardPoolFencingDecorativeStyle'));
const ChildgardGardenFencingStandardStyles = lazy(() => import('./pages/public/products/ChildgardGardenFencingStandardStyles'));
const ChildgardGardenFencingDecorativeStyles = lazy(() => import('./pages/public/products/ChildgardGardenFencingDecorativeStyles'));
const ChildgardGardenFencingSteelPicket = lazy(() => import('./pages/public/products/ChildgardGardenFencingSteelPicket'));

// Hardware Sub-pages
const StainlessSteelFittings = lazy(() => import('./pages/public/products/hardware/StainlessSteelFittings'));
const Caps = lazy(() => import('./pages/public/products/hardware/Caps'));
const Cement = lazy(() => import('./pages/public/products/hardware/Cement'));
const DecorativeFittings = lazy(() => import('./pages/public/products/hardware/DecorativeFittings'));
const Fasteners = lazy(() => import('./pages/public/products/hardware/Fasteners'));
const Hinges = lazy(() => import('./pages/public/products/hardware/Hinges'));
const Locks = lazy(() => import('./pages/public/products/hardware/Locks'));
const Paints = lazy(() => import('./pages/public/products/hardware/Paints'));
const PostAccessories = lazy(() => import('./pages/public/products/hardware/PostAccessories'));
const Tools = lazy(() => import('./pages/public/products/hardware/Tools'));
const SlidingGateFittings = lazy(() => import('./pages/public/products/hardware/SlidingGateFittings'));
const SPFAccessories = lazy(() => import('./pages/public/products/hardware/SPFAccessories'));

/**
 * Main App Component
 * 
 * Sets up the application's theme provider and public website routing.
 * All routes are public-facing - authentication and portals are in separate apps.
 */
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<PageLoadingOverlay />}>
            <Routes>
              <Route path="/" element={<PublicLayout />}>
                <Route index element={<PublicHome />} />
              
              {/* Authentication */}
              <Route path="auth" element={<AuthPage />} />
              
              {/* Company & Location Information */}
              <Route path="outlets" element={<OutletsPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="about/history" element={<HistoryPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="articles" element={<ArticlesPage />} />
              <Route path="articles/:id" element={<ArticleDetailPage />} />
              
              {/* Product Specifications & Documentation */}
              <Route path="specification/colours" element={<ColoursPage />} />
              <Route path="specification/technical" element={<TechnicalPage />} />
              <Route path="specification/brochures" element={<BrochuresPage />} />
              <Route path="specification/installation" element={<InstallationPage />} />
              <Route path="specification/warranty" element={<WarrantyPage />} />
              <Route path="specification/videos" element={<VideosPage />} />
              
              {/* Public Product Catalog */}
              <Route path="products/spf-steel" element={<SPFSteel />} />
              <Route path="products/steel-plinth" element={<SteelPlinth />} />
              <Route path="products/gramlat-3d" element={<GramLat3D />} />
              <Route path="products/floodway-louvres" element={<FloodwayLouvres />} />
              <Route path="products/gramline-infill-sheet" element={<GramlineInfillSheet />} />
              <Route path="products/colourline-infill-sheet" element={<ColourLineInfillSheet />} />
              <Route path="products/slimline-framing" element={<SlimlineFraming />} />
              <Route path="products/smartpost-framing-post" element={<SmartPostFramingPost />} />
              <Route path="products/steel-plinth-retaining-walls" element={<SteelPlinthRetainingWalls />} />
              <Route path="products/gates-by-gramline" element={<GatesByGramline />} />
              <Route path="products/accessories" element={<PublicAccessories />} />
              <Route path="products/smartslat" element={<SmartSlat />} />
              <Route path="products/gramslat-extension-kit" element={<GramslatExtensionKit />} />
              <Route path="products/security-fencing" element={<SecurityFencing />} />
              <Route path="products/hardware" element={<Hardware />} />
              <Route path="products/pool-fencing" element={<ChildgardPoolFencing />} />
              <Route path="products/garden-fencing" element={<ChildgardGardenFencing />} />
              <Route path="products/gramlat-3d/punched-lattice" element={<GramLat3DPunchedLattice />} />
              <Route path="products/pool-fencing/standard-style" element={<ChildgardPoolFencingStandardStyle />} />
              <Route path="products/pool-fencing/decorative-style" element={<ChildgardPoolFencingDecorativeStyle />} />
              <Route path="products/garden-fencing/standard-styles" element={<ChildgardGardenFencingStandardStyles />} />
              <Route path="products/garden-fencing/decorative-styles" element={<ChildgardGardenFencingDecorativeStyles />} />
              <Route path="products/garden-fencing/steel-picket" element={<ChildgardGardenFencingSteelPicket />} />
              
              {/* Hardware Sub-pages */}
              <Route path="products/hardware/stainless-steel-fittings" element={<StainlessSteelFittings />} />
              <Route path="products/hardware/caps" element={<Caps />} />
              <Route path="products/hardware/cement" element={<Cement />} />
              <Route path="products/hardware/decorative-fittings" element={<DecorativeFittings />} />
              <Route path="products/hardware/fasteners" element={<Fasteners />} />
              <Route path="products/hardware/hinges" element={<Hinges />} />
              <Route path="products/hardware/locks" element={<Locks />} />
              <Route path="products/hardware/paints" element={<Paints />} />
              <Route path="products/hardware/post-accessories" element={<PostAccessories />} />
              <Route path="products/hardware/tools" element={<Tools />} />
              <Route path="products/hardware/sliding-gate-fittings" element={<SlidingGateFittings />} />
              <Route path="products/hardware/spf-accessories" element={<SPFAccessories />} />
              
              {/* Business & Trade Services */}
              <Route path="purchasing/quote" element={<QuoteRequest />} />
              
              {/* 404 Not Found */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/**
 * Display ASCII art branding in browser console
 */
function consoleMessage() {
    console.log(`
  ██████╗ ██████╗  █████╗ ███╗   ███╗██╗     ██╗███╗   ██╗███████╗
 ██╔════╝ ██╔══██╗██╔══██╗████╗ ████║██║     ██║████╗  ██║██╔════╝
 ██║  ███╗██████╔╝███████║██╔████╔██║██║     ██║██╔██╗ ██║█████╗  
 ██║   ██║██╔══██╗██╔══██║██║╚██╔╝██║██║     ██║██║╚██╗██║██╔══╝  
 ╚██████╔╝██║  ██║██║  ██║██║ ╚═╝ ██║███████╗██║██║ ╚████║███████╗
  ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝     ╚═╝╚══════╝╚═╝╚═╝  ╚═══╝╚══════╝

        No.1 FOR STEEL PRIVACY FENCING | gram.com.au
`);
}

// Initialize console branding
consoleMessage();

export default App;
